<template>
  <div class="app-container">
    <transition name="delay">
      <div class="app-container__hidden-menu" v-show="showLeftSidebar || showRightSidebar">
        <transition name="fade">
          <div class="app-container__hidden-menu-bg" v-show="showLeftSidebar || showRightSidebar" @click="hideSidebars()"></div>
        </transition>
        <transition name="slide-right">
          <div class="app-container__hidden-menu-left" v-show="showLeftSidebar" :style="{ height: `calc(${screen.height + 'px'} - 6rem)` }">
            <div class="app-container__hidden-menu-left-close icon-close text-color-white text--xxx-large" @click="hideSidebars()"></div>
            <slot name="nav"></slot>
          </div>
        </transition>
        <transition name="slide-left">
          <div class="app-container__hidden-menu-right" v-show="showRightSidebar" :style="{ height: `calc(${screen.height + 'px'} - 6rem)` }">
            <div class="app-container__hidden-menu-right-close icon-close text-color-white text--xxx-large" @click="hideSidebars()"></div>
            <slot name="nav"></slot>
          </div>
        </transition>
      </div>
    </transition>

    <div class="app-container__header">
      <slot name="header"></slot>
      <div class="divider"></div>
    </div>

    <div class="app-container__main">
      <div class="app-container__menu" :style="{ height: mainHeight + 'px' }">
        <slot name="nav"></slot>
      </div>

      <div class="app-container__main-inner" :style="{ height: mainHeight + 'px' }">
        <div class="app-container__header-sub">
          <div class="app-container__header-sub_controls d-flex align-center justify-end" v-if="!screen.breakPoint.tabPort"
               :style="{ opacity: animation.opacity.subPageControls, visibility: animation.opacity.subPageControls === 0 ? 'hidden' : 'visible' }"
          >
            <language-control class="mr-1"></language-control>
            <chat-control v-if="showChat" class="mr-1"></chat-control>
            <notify-control class="mr-1"></notify-control>
            <settings-control></settings-control>
          </div>
          <portal-target
            :style="{
              transition: 'all .2s',
              opacity: animation.opacity.subPageControls === 0 ? '1' : '0',
              visibility: animation.opacity.subPageControls === 0 ? 'visible' : 'hidden',
              transform: animation.opacity.subPageControls === 0 ? 'translateY(0)' : 'translateY(-40%)',
          }"
            name="subPageHeader2"
            class="app-container__header-sub-headline"
            :slot-props="{ headerFontSize: 18, paddingTop: 1.5, scrollTop, opacity: animation.opacity.subPageControls }"
          >
          </portal-target>

          <portal-target
            name="subPageHeader"
            class="app-container__header-sub-headline"
            :slot-props="{ headerFontSize: 30, paddingTop: 1.5, scrollTop, opacity: animation.opacity.subPageControls }"
            v-if="screen.breakPoint.tabPort"
          >
          </portal-target>
        </div>

        <div
          class="app-container__content"
          :style="{
            height: contentHeight + 'px'
          }"
          @scroll="onScroll"
          v-scrollable
          style="-webkit-overflow-scrolling: auto;"
        >
          <div v-if="!screen.breakPoint.tabPort" :style="{ opacity: animation.opacity.subPageControls }">
            <portal-target name="subPageHeader" class="app-container__header-sub-headline" :slot-props="{ headerFontSize: 30, paddingTop: 3, scrollTop }"></portal-target>
          </div>
          <div class="app-container__content_left">
            <div class="app-container__content-inner">
              <div class="app-container__content-inner-actions">
                <portal-target name="heroBlock" class="app-container__hero-block" :slot-props="{ opacity: 1 }"></portal-target>
                <portal-target name="subPageControls" class="app-container__controls" :slot-props="{ opacity: 1 }" v-if="!screen.breakPoint.tabLand || !hasSidebar"></portal-target>
              </div>
              <div class="app-container__content-inner-sub">
                <div class="app-container__content-inner-sub_left" :class="hasSidebar ? 'app-container__content-inner-sub_left--has-sidebar' : ''">
                  <slot></slot>
                </div>
                <div class="app-container__content-inner-sub_right" :class="hasSidebar ? 'app-container__content-inner-sub_right--has-sidebar' : ''" v-if="screen.breakPoint.tabLand && hasSidebar">
<!--                  <div :style="{ top: scrollTop + 'px' }">-->
<!--                  <div :style="{ top: 0 }">-->
                  <div>
                    <portal-target name="subPageControls" :slot-props="{ scrollTop }"></portal-target>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="app-container__footer" v-show="!screen.breakPoint.tabPort">
          <slot name="mobileNav"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NotifyControl from '@/ui/controls/NotifyControl'
import ChatControl from '@/ui/controls/ChatControl'
import LanguageControl from '@/ui/controls/LanguageControl'
import SettingsControl from '@/ui/controls/SettingsControl'

import Scrollable from '@/directives/scrollable'

import { Capacitor } from '@capacitor/core'

import screen from '@/mixins/screen'
import sidebars from '@/mixins/sidebars'

export default {
  mixins: [screen, sidebars],

  directives: {
    Scrollable
  },

  components: {
    NotifyControl,
    ChatControl,
    LanguageControl,
    SettingsControl
  },

  data () {
    return {
      headerHeight: 0,
      mainHeight: 0,
      contentHeight: 0,
      subHeaderHeight: 0,
      footer: 0,

      subPageControlsHeight: 0,
      heroHeight: 0,
      controlsHeight: 0,

      scrollTop: 0,

      narrow: false,

      showChat: !Capacitor.isNativePlatform()
    }
  },

  methods: {
    onScroll (e) {
      const scrollHeight = Math.round(e.target.scrollHeight)
      const scrollTop = Math.round(e.target.scrollTop)
      const contentHeight = Math.round(e.target.clientHeight)

      if (scrollHeight === (scrollTop + contentHeight + 1) || scrollHeight === (scrollTop + contentHeight - 1) || scrollHeight === (scrollTop + contentHeight)) {
        const event = new Event('reachedBottom')
        document.dispatchEvent(event)
      }
      this.scrollTop = e.target.scrollTop
    },

    computeHeights () {
      this.$nextTick(() => {
        const header = document.querySelector('.app-container__header')
        const subHeader = document.querySelector('.app-container__header-sub')
        const footer = document.querySelector('.app-container__footer')
        const controls = document.querySelector('.app-container__controls')
        const subPageControls = document.querySelector('.app-container__header-sub_controls')
        const hero = document.querySelector('.app-container__hero-block')

        if (header && subHeader && footer) {
          this.headerHeight = header.clientHeight
          this.subHeaderHeight = subHeader.clientHeight
          this.footerHeight = footer.clientHeight

          this.mainHeight = this.screen.height - this.headerHeight
          this.contentHeight = this.mainHeight - this.subHeaderHeight - this.footerHeight
        }

        if (controls) {
          this.controlsHeight = controls.clientHeight
        }
        if (hero) {
          this.heroHeight = hero.clientHeight
        }
        if (subPageControls) {
          this.subPageControlsHeight = subPageControls.clientHeight
        }
      })
    }
  },

  mounted () {
    this.computeHeights()
    this.narrow = this.$route.name !== 'dashboard' || this.$route.name !== 'orders'
  },

  updated () {
    this.computeHeights()
  },

  computed: {
    animation () {
      const isTabPort = this.screen.breakPoint.tabPort

      const scrollTop = this.scrollTop
      const controlsHeight = this.controlsHeight
      const subPageControlsHeight = this.subPageControlsHeight
      const heroHeight = this.heroHeight

      const controlsOpacityDifference = (controlsHeight - ((scrollTop - heroHeight) * 1.2)) / (controlsHeight / 100) / 100
      const subPageControlsOpacityDifference = (subPageControlsHeight - (scrollTop)) / (subPageControlsHeight / 100) / 100
      const heroOpacityDifference = (heroHeight - (scrollTop * 1.2)) / (heroHeight / 100) / 100

      const controlsOpacity = controlsOpacityDifference >= 0 ? controlsOpacityDifference : 0
      const subPageControls = subPageControlsOpacityDifference >= 0 ? subPageControlsOpacityDifference : 0
      const heroOpacity = heroOpacityDifference >= 0 ? heroOpacityDifference : 0

      const minFontSize = 18
      const maxFontSize = 30
      let fontSize = 30

      const fontSizeLoc = maxFontSize - ((maxFontSize - minFontSize) / 300 * scrollTop)
      if (fontSizeLoc <= minFontSize) {
        fontSize = minFontSize
      } else if (fontSizeLoc >= maxFontSize) {
        fontSize = maxFontSize
      } else {
        fontSize = fontSizeLoc
      }

      fontSize = maxFontSize

      const minPaddingTop = 1.5
      const maxPaddingTop = 8
      let paddingTop = 8

      const paddingTopLoc = maxPaddingTop - ((maxPaddingTop - minPaddingTop) / 50 * scrollTop)
      if (paddingTopLoc < minPaddingTop) {
        paddingTop = minPaddingTop
      } else if (paddingTopLoc >= maxPaddingTop) {
        paddingTop = maxPaddingTop
      } else {
        paddingTop = paddingTopLoc
      }
      paddingTop = minPaddingTop

      return {
        headerFontSize: isTabPort ? maxFontSize : fontSize,
        paddingTop: isTabPort ? minPaddingTop : paddingTop,
        opacity: {
          controlsOpacity,
          subPageControls,
          heroOpacity
        }
      }
    },

    hasSidebar () {
      return this.$route && this.$route.meta && this.$route.meta.sidebar
    }
  },

  watch: {
    'screen.height' () {
      this.computeHeights()
    },

    'screen.width' () {
      this.computeHeights()
    },

    'route.name' (val) {
      this.narrow = val !== 'dashboard' || this.$route.name !== 'orders'
      // this.computeHeights()
    },

    scrollTop (val) {
      // this.computeHeights()
    }
  }
}
</script>

<style lang="scss">
.app-container {
  &__main {
    overflow-y: hidden;

    @include respond(small-desktop) {
      display: flex;
    }

    &-inner {
      overflow-y: hidden;
      flex-basis: 100%;
      overflow-x: hidden;
    }
  }

  &__menu {
    display: none;
    overflow-y: auto;

    @include respond(small-desktop) {
      display: block;
      width: $sidebar-width;
      border-right: 1px $color-space-lighten solid;
      background-color: $color-grey-extra-light;
      //box-shadow: 1px 0 6px rgba(52, 48, 61, 0.08);
      z-index: 1;
    }
  }

  &__hidden-menu {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 3;
    overflow-y: auto;

    &-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba($color-black, .75);
    }

    &-left {
      position: absolute;
      background-color: $color-white;
      top: 6rem;
      left: 0;
      width: $sidebar-width;
      overflow-y: auto;

      &-close {
        display: none;
        position: absolute;
        top: $default-spacing;
        right: -5rem;
        z-index: 3;
      }
    }

    &-right {
      position: absolute;
      background-color: $color-white;
      top: 6rem;
      right: 0;
      width: $sidebar-width;
      overflow-y: auto;

      &-close {
        position: absolute;
        top: $default-spacing;
        left: -5rem;
        z-index: 3;
      }
    }
  }

  &__header {
    display: none;
    //box-shadow: $elevation;
    position: relative;
    z-index: 3;
    box-sizing: content-box;

    @include respond(tab-port) {
      display: block;
    }

    &-sub {
      position: relative;
      z-index: 4;

      @include respond(tab-port) {
        z-index: 2;
      }

      &_controls {
        position: absolute;
        width: 100%;
        top: $default-spacing-mobile;
        /*left: $default-spacing-mobile;*/
        padding-right: $default-spacing-mobile;
        padding-left: $default-spacing-mobile;
        z-index: 3;

        @include iosTop($default-spacing-mobile);
      }
    }
  }

  &__content {
    overflow-y: auto;

    @include respond(tab-land) {
      box-shadow: inset 1px 0 6px rgba(52, 48, 61, 0.08);
    }

    &-inner {
      &-sub {
        padding-left: $default-spacing-mobile;
        padding-right: $default-spacing-mobile;

        @include respond(tab-port) {
          padding-left: $default-spacing;
          padding-right: $default-spacing;
        }

        max-width: 120rem;
        margin: 2rem auto;

        /*&--narrow {*/
        /*  max-width: 78rem;*/
        /*}*/

        @include respond(tab-land) {
          display: flex;

          &_left {
            flex-basis: 100%;

            &--has-sidebar {
              padding-right: 2rem;
              flex-basis: 70%;
            }
          }

          &_right {
            display: none;
            flex-basis: 0;
            position: relative;

            &--has-sidebar {
              display: block;
              flex-basis: 30%;
              padding-left: 1rem;
            }

            & > * {
              /*position: absolute;*/
              /*left: 0;*/
            }
          }
        }
      }
    }

    @include respond(small-desktop) {
    }
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: $color-white;
    box-shadow: $elevation-reverse;
  }
}
</style>
